export type ExecutionStatus = "pending" | "running" | "completed" | "failed" | "cancelled" | "paused" | (string & {});

export const TERMINAL_STATUSES: readonly ExecutionStatus[] = ["completed", "failed", "cancelled"];

export interface NodeResult {
  status?: ExecutionStatus;
  output?: unknown;
  error?: string;
  [key: string]: unknown;
}

/** `GET /api/v2/chains/executions/{id}` */
export interface ChainExecution {
  id: string;
  chain_id?: string;
  status: ExecutionStatus;
  input_data?: Record<string, unknown>;
  node_results?: Record<string, NodeResult> | null;
  error?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  execution_time_ms?: number | null;
  total_tokens?: number | null;
  total_cost?: number | null;
  total_ai_credits_used?: number | null;
  passio_request_id?: string | null;
}

/** `POST /api/v2/chains/{id}/execute` returns immediately. */
export interface ExecutionStarted {
  id: string;
  status: ExecutionStatus;
  chain_id?: string;
}

export interface NodeProgress {
  nodeId: string;
  status: ExecutionStatus;
}

export interface RunProgress {
  status: ExecutionStatus;
  elapsedMs: number;
  polls: number;
  nodes: NodeProgress[];
  execution?: ChainExecution;
}

/**
 * One chain run (execute plus polling) for inspectors and logs, reported to `onCall` as it starts,
 * on every poll and when it ends. `status` is the execution's, or `error` / `stopped` when the call
 * itself failed or the caller stopped waiting. Never contains the API key.
 */
export interface ChainCallRecord {
  kind: "chain";
  id: string;
  chainId: string;
  /** Registry key and title when run through `runDemoChain`. */
  chainKey?: string;
  label?: string;
  executionId?: string;
  /** Execute URL as called (may be a proxy). */
  url: string;
  startedAt: string;
  durationMs: number;
  requestBody: { input_data: Record<string, unknown> };
  status: ExecutionStatus | "error" | "stopped";
  polls: number;
  execution?: ChainExecution;
  sinkNodeId?: string;
  /** HTTP status of the call that failed, when one did. */
  httpStatus?: number;
  error?: string;
}

export interface TempUpload {
  public_url: string;
  [key: string]: unknown;
}

export type ModelTier = "frontier" | "pro" | "fast" | "lite" | (string & {});

export interface ModelTierInfo {
  id: ModelTier;
  label: string;
  summary: string;
}

/** One text model from `GET /api/v2/models/catalog`. Ratings are 1-5 relative to the other models. */
export interface CatalogModel {
  api_name: string;
  display_name: string;
  provider: string;
  description?: string | null;
  context_window?: number | null;
  supports_vision: boolean;
  zero_data_retention: boolean;
  variant_of?: string | null;
  deprecated: boolean;
  replacement?: string | null;
  tier: ModelTier;
  /** `cost` is 1 (cheapest) to 5, from the blended list price; null when the model has no price. */
  ratings: { intelligence: number; speed: number; cost: number | null };
  /** No editorial profile yet: tier and ratings were estimated from price. */
  estimated: boolean;
  /** Provider list price in USD per 1M tokens; blended assumes 3 input tokens per output token. */
  price: { input_per_mtok_usd: number; output_per_mtok_usd: number; blended_per_mtok_usd: number } | null;
  /** Closest models in the same tier, other providers first. */
  similar_to: string[];
}

export interface ModelCatalog {
  tiers: ModelTierInfo[];
  rating_scale: Record<string, string>;
  models: CatalogModel[];
}

/** One model's results on a platform route's tests. Scores and rates are 0-100. */
export interface TestedModel {
  model: string;
  accuracy: number | null;
  pass_rate: number | null;
  success_rate: number | null;
  p50_s: number | null;
  p95_s: number | null;
  credits_per_run: number | null;
  runs: number | null;
}

export type ModelPickKind = "recommended" | "quality" | "budget";

export interface ModelPick extends TestedModel {
  why: string | null;
}

/** A published platform route from `GET /api/v2/tests/published/platform-models`. */
export interface PublishedRoute {
  function_name: string;
  platform_tool: string | null;
  kind: string | null;
  /** Nutrition API paths this route serves, e.g. `/napi/tools/extractIngredients`. */
  api_paths: string[];
  default_model: string | null;
  tested_at: string;
  cases: number;
  repeats: number;
  picks: Record<ModelPickKind, ModelPick | null>;
  note: string | null;
  bar: Record<string, number> | null;
  /** Every tested model, most accurate first. */
  models: TestedModel[];
}

export interface PublishedPlatformModels {
  routes: PublishedRoute[];
  generated_at: string;
}

export type FeedbackSourceType = "function_run" | "chain_execution";

/** One item for `POST /api/v2/feedback/batch`. */
export interface EasyAIFeedbackItem {
  source_type: FeedbackSourceType;
  source_id: string;
  /** thumbs_up, thumbs_down, accepted, rejected, edited, correction, click, purchase... */
  label: string;
  score?: number;
  note?: string;
  item_ref?: string;
  metadata?: Record<string, unknown>;
}
